import { Task } from '@/lib/types';
import { getSession } from 'next-auth/react';
import { toast } from '@/components/ui/toast';
import { handleClientDownload } from '@/lib/utils/downloads';
import {
  type DownloadFormat,
  type DownloadResult,
} from '@/lib/types/downloads';

const SAFE_DOWNLOAD_PROTOCOLS = new Set(['https:']);
const ALLOWED_DOWNLOAD_HOST_SUFFIXES = ['amazonaws.com', 's3.amazonaws.com', 'bytemethod.ai'];

function isPrivateHostname(hostname: string): boolean {
  if (hostname === 'localhost') {
    return true;
  }

  const ipv4 = hostname.match(/^(\d{1,3})(?:\.(\d{1,3})){3}$/);
  if (!ipv4) {
    return false;
  }

  const octets = hostname.split('.').map((segment) => Number(segment));
  if (octets[0] === 10) return true;
  if (octets[0] === 127) return true;
  if (octets[0] === 169 && octets[1] === 254) return true;
  if (octets[0] === 192 && octets[1] === 168) return true;
  if (octets[0] === 172 && octets[1] >= 16 && octets[1] <= 31) return true;

  return false;
}

function isAllowedDownloadHost(hostname: string): boolean {
  if (typeof window !== 'undefined' && hostname === window.location.hostname) {
    return true;
  }

  return ALLOWED_DOWNLOAD_HOST_SUFFIXES.some((suffix) =>
    hostname === suffix || hostname.endsWith(`.${suffix}`),
  );
}

function resolveDownloadUrl(rawUrl: string): string {
  const base = typeof window !== 'undefined' ? window.location.origin : undefined;

  let parsed: URL;
  try {
    parsed = new URL(rawUrl, base);
  } catch {
    throw new Error('Download URL is invalid.');
  }

  if (!SAFE_DOWNLOAD_PROTOCOLS.has(parsed.protocol)) {
    throw new Error('Unsupported download protocol.');
  }

  if (isPrivateHostname(parsed.hostname)) {
    throw new Error('Private network downloads are not permitted.');
  }

  if (!isAllowedDownloadHost(parsed.hostname)) {
    throw new Error('Download host is not permitted.');
  }

  return parsed.toString();
}

class BackgroundTaskService {
  private tasks = new Map<string, Task>();
  private websocket: WebSocket | null = null;
  private keepaliveTimer: NodeJS.Timeout | null = null;

  // Add a new task
  async addTask(task: Omit<Task, 'id' | 'createdAt'>): Promise<string> {
    const id = `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newTask: Task = {
      ...task,
      id,
      createdAt: new Date(),
    };
    this.tasks.set(id, newTask);
    const session = await getSession();
    const websocketUrl =
      process.env.NEXT_PUBLIC_WS_URL + '?userId=' + session?.user?.id; // Adjust URL as needed

    // Connect websocket if not already connected
    this.ensureWebSocketConnection(websocketUrl);

    return id;
  }

  // Update an existing task
  updateTask(id: string, updates: Partial<Task>): void {
    const task = this.tasks.get(id);
    if (task) {
      const updatedTask = { ...task, ...updates };
      if (updates.status === 'completed' && !task.completedAt) {
        updatedTask.completedAt = new Date();
      }
      this.tasks.set(id, updatedTask);
    }
  }

  // Remove a task
  removeTask(id: string): void {
    this.tasks.delete(id);
  }

  // Get a specific task
  getTask(id: string): Task | undefined {
    return this.tasks.get(id);
  }

  // Get all tasks
  getAllTasks(): Task[] {
    return Array.from(this.tasks.values());
  }

  // Get tasks by status
  getTasksByStatus(status: Task['status']): Task[] {
    return Array.from(this.tasks.values()).filter(
      (task) => task.status === status,
    );
  }

  // Clear completed tasks
  clearCompleted(): void {
    const completedTasks = Array.from(this.tasks.values()).filter(
      (task) => task.status === 'completed',
    );
    completedTasks.forEach((task) => this.tasks.delete(task.id));
  }

  // Clear all tasks
  clearAll(): void {
    this.tasks.clear();
    this.disconnectWebSocket();
  }

  // Ensure websocket connection
  private ensureWebSocketConnection(websocketUrl: string): void {
    if (this.websocket?.readyState === WebSocket.OPEN) {
      // Send a keepalive message to keep the websocket connection alive
      this.websocket.send(JSON.stringify({ type: 'keepalive' }));
      return; // Already connected
    }

    try {
      this.websocket = new WebSocket(websocketUrl);

      this.websocket.onopen = () => {
        console.log('WebSocket connected');
        this.startKeepaliveTimer();
      };

      this.websocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.taskId && data.task) {
            if (data.file) {
              const targetFormat: DownloadFormat =
                data.file.targetFormat || 'docx';
              const filename: string =
                data.file.filename ||
                `document.${targetFormat === 'md' ? 'md' : 'docx'}`;
              data.task.actions = [
                {
                  id: 'download',
                  label: 'Download',
                  onClick: () => {
                    void (async () => {
                      try {
                        toast.info('Preparing Download', 'Fetching generated file...');

                        const safeUrl = resolveDownloadUrl(data.file.url);
                        const response = await fetch(safeUrl);
                        if (!response.ok) {
                          throw new Error('Failed to load generated file.');
                        }

                        const downloadResult: DownloadResult = {
                          success: true,
                          filename,
                        };

                        const sourceFormat: string = data.file.sourceFormat || 'md';

                        if (sourceFormat === 'md') {
                          downloadResult.markdownContent = await response.text();
                        } else {
                          downloadResult.blob = await response.blob();
                        }

                        const outcome = await handleClientDownload(downloadResult, {
                          format: targetFormat,
                          defaultFilename: filename,
                          docxOptions: {
                            mermaidSupport: true,
                            theme: 'default',
                            fontFamily: 'Arial',
                          },
                        });

                        if (!outcome.success) {
                          throw new Error(outcome.error || 'Download failed.');
                        }

                        toast.success('Download Complete', `${filename} downloaded.`);
                      } catch (error) {
                        console.error('Failed to download generated document:', error);
                        toast.error(
                          'Download Failed',
                          error instanceof Error
                            ? error.message
                            : 'Unable to download generated document.',
                        );
                      }
                    })();
                  },
                  variant: 'default',
                },
              ];
            }
            this.updateTask(data.taskId, data.task);
          }
        } catch (error) {
          console.error('Failed to parse websocket message:', error);
        }
      };

      this.websocket.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      this.websocket.onclose = () => {
        this.clearKeepaliveTimer();
        this.websocket = null;
      };
    } catch (error) {
      console.error('Failed to connect to websocket:', error);
    }
  }

  // Start keepalive timer
  private startKeepaliveTimer(): void {
    this.clearKeepaliveTimer();
    this.keepaliveTimer = setInterval(() => {
      if (this.websocket?.readyState === WebSocket.OPEN) {
        this.websocket.send(JSON.stringify({ type: 'keepalive' }));
      } else {
        this.clearKeepaliveTimer();
      }
    }, 60000); // 60 seconds
  }

  // Clear keepalive timer
  private clearKeepaliveTimer(): void {
    if (this.keepaliveTimer) {
      clearInterval(this.keepaliveTimer);
      this.keepaliveTimer = null;
    }
  }

  // Disconnect websocket
  private disconnectWebSocket(): void {
    this.clearKeepaliveTimer();
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
  }
}

// Export singleton instance
export const backgroundTaskService = new BackgroundTaskService();
